(() => {
  if (customElements.get('ms-performance-addons')) return;

  class MsPerformanceAddons extends HTMLElement {
    /**
     * Umbrales por tamaño de frasco. Salen del widget que la tienda ya usa hoy
     * (30 ml → sugerido 3 / bestia 5 · 60 ml → 6 / 9 · 100 ml → 10 / 15): 3 g en
     * un frasco de 30 ml no es lo mismo que en uno de 100 ml, así que el nombre
     * del nivel tiene que moverse con la talla elegida. Formato "ml: sug, top".
     */
    #sizeMap() {
      return (this.dataset.sizeMap || '')
        .split(/[\r\n]+/)
        .map((line) => {
          const m = line.match(/^\s*(\d+)\s*:\s*(\d+)\s*,\s*(\d+)\s*$/);
          return m ? { ml: +m[1], sug: +m[2], top: +m[3] } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.ml - b.ml);
    }

    /** Cuatro niveles "nombre | descripción": base, algo, sugerido, tope. */
    #levels() {
      return (this.dataset.levels || '')
        .split(/[\r\n]+/)
        .map((line) => {
          const i = line.indexOf('|');
          if (i < 0) return line.trim() ? { name: line.trim(), desc: '' } : null;
          return { name: line.slice(0, i).trim(), desc: line.slice(i + 1).trim() };
        })
        .filter(Boolean);
    }

    /**
     * Mililitros del frasco elegido AHORA. Se resuelve por id de variante contra
     * el mapa que emite Liquid — no raspando el texto del selector, que cambia de
     * markup entre temas y se rompe con el morph de variante.
     */
    #ml() {
      let map = {};
      try { map = JSON.parse(this.dataset.variantMl || '{}'); } catch (_) { map = {}; }
      const scope = this.closest('.shopify-section') || document;
      const field = scope.querySelector('[name="id"]:checked, input[name="id"], select[name="id"]');
      const id = field && field.value;
      if (id && map[id]) return map[id];
      const url = new URLSearchParams(location.search).get('variant');
      if (url && map[url]) return map[url];
      const vals = Object.values(map);
      return vals.length ? vals.sort((a, b) => b - a)[0] : 0;
    }

    /** Umbrales del frasco actual; el renglón más grande cubre lo que sobre. */
    #thresholds() {
      const rows = this.#sizeMap();
      if (!rows.length) return { sug: 0, top: 0 };
      const ml = this.#ml();
      return rows.find((r) => ml <= r.ml) || rows[rows.length - 1];
    }

    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-ms-addon]'));
      this.totalEl = this.querySelector('[data-ms-addons-total]');
      try {
        // `narrowSymbol` + locale CON REGIÓN son los dos que hacen falta para
        // que diga "+$89" y no "+89 MXN": el <html lang="es"> pelón no basta.
        this.fmt = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'es-MX', {
          style: 'currency',
          currency: this.dataset.currency || 'MXN',
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 0,
        });
      } catch (e) {
        this.fmt = { format: (n) => '$' + Math.round(n) };
      }

      this.addEventListener('click', (ev) => {
        // El globo "qué es" se abre solo con hover en escritorio (CSS). En móvil
        // no hay hover, así que el tap lo alterna aquí vía aria-expanded — que es
        // el mismo gancho que usa el CSS, sin clases paralelas.
        const help = ev.target.closest('[data-ms-help]');
        if (help) {
          ev.preventDefault();
          const wasOpen = help.getAttribute('aria-expanded') === 'true';
          this.#closeTips();
          if (!wasOpen) help.setAttribute('aria-expanded', 'true');
          return;
        }

        // Interruptor: prende con la primera cantidad ofrecida, apaga a 0.
        const sw = ev.target.closest('[data-ms-switch]');
        if (sw) {
          ev.preventDefault();
          const row = sw.closest('[data-ms-addon]');
          if (!row) return;
          const on = Number(row.dataset.quantity) > 0;
          // Al prender arranca en la cantidad SUGERIDA para el frasco elegido,
          // no en un número fijo: es el atajo de un toque al valor correcto.
          const { sug } = this.#thresholds();
          const max = Number(row.dataset.max) || 20;
          this.#set(row, on ? 0 : Math.min(sug || 5, max));
          return;
        }

        const btn = ev.target.closest('button[data-step], button[data-set]');
        if (!btn) return;
        ev.preventDefault();
        const row = btn.closest('[data-ms-addon]');
        if (!row) return;
        const max = Number(row.dataset.max) || 30;
        const cur = Number(row.dataset.quantity) || 0;
        // En el stepper "+5" SUMA sobre lo que ya hay: es un atajo, no un
        // reemplazo. En el interruptor las cantidades son EXCLUYENTES (5 ó 10
        // ó 15), así que ahí el chip fija en vez de acumular.
        let next;
        if (btn.hasAttribute('data-set')) {
          next = row.dataset.control === 'switch' ? Number(btn.dataset.set) : cur + Number(btn.dataset.set);
        } else {
          next = cur + Number(btn.dataset.step);
        }
        this.#set(row, Math.min(Math.max(next, 0), max));
      });

      // El deslizador escribe directo en data-quantity, que es el contrato que
      // lee product-form.js. `input` (no `change`) para que el nombre del nivel
      // y el precio se muevan MIENTRAS arrastras, no al soltar.
      this.addEventListener('input', (ev) => {
        const range = ev.target.closest('[data-ms-range]');
        if (!range) return;
        const row = range.closest('[data-ms-addon]');
        if (row) this.#set(row, Number(range.value) || 0);
      });

      // Al cambiar de talla cambian los umbrales (3 g es "bestia" en 30 ml y
      // apenas "un poco más" en 100 ml), así que hay que repintar los nombres.
      // El tema no emite un evento propio fiable aquí: se escucha el cambio del
      // campo de variante en la sección, que es lo que de verdad se mueve.
      this.onVariant = () => this.#render();
      const scope = this.closest('.shopify-section') || document;
      scope.addEventListener('change', this.onVariant);

      // Un globo abierto por tap tiene que cerrarse al tocar en otro lado o con
      // Escape; si no, en móvil se queda tapando la fila de abajo.
      this.onOutside = (ev) => {
        if (!this.contains(ev.target)) this.#closeTips();
      };
      this.onEsc = (ev) => {
        if (ev.key === 'Escape') this.#closeTips();
      };
      document.addEventListener('click', this.onOutside);
      document.addEventListener('keydown', this.onEsc);

      this.#render();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onEsc);
      const scope = this.closest('.shopify-section') || document;
      if (this.onVariant) scope.removeEventListener('change', this.onVariant);
    }

    #closeTips() {
      this.querySelectorAll('[data-ms-help][aria-expanded="true"]').forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
      });
    }

    #set(row, qty) {
      row.dataset.quantity = String(qty);
      this.#render();
    }


    /**
     * Pinta el deslizador: relleno, punto, nombre del nivel y lo que suma.
     * El nivel se elige contra los umbrales DEL FRASCO ACTUAL, no contra
     * números fijos — es la parte que el widget viejo ya hacía y que se
     * perdería si el deslizador se etiquetara sólo con gramos.
     */
    #renderSlider(row, qty, max) {
      const { sug, top } = this.#thresholds();
      const levels = this.#levels();
      this.#paintTrack(row, qty, max);
      const range = row.querySelector('[data-ms-range]');

      // Marca del nivel sugerido: es el atajo visual al "el más pedido".
      const mark = row.querySelector('[data-ms-mark]');
      if (mark) {
        const show = sug > 0 && sug < max;
        mark.hidden = !show;
        if (show) mark.style.insetInlineStart = (sug / max) * 100 + '%';
      }

      let idx = 0;
      if (qty > 0) idx = 1;
      if (sug && qty >= sug) idx = 2;
      if (top && qty >= top) idx = 3;
      const level = levels[Math.min(idx, levels.length - 1)] || { name: '', desc: '' };

      const unitPrice = Number(row.dataset.unitPrice) || 0;
      const nameEl = row.querySelector('[data-ms-level-name]');
      const addEl = row.querySelector('[data-ms-level-add]');
      const descEl = row.querySelector('[data-ms-level-desc]');
      const unit = row.dataset.addonUnit || '';
      if (nameEl) nameEl.textContent = qty > 0 ? level.name + ' · ' + qty + ' ' + unit : level.name;
      if (addEl) addEl.textContent = qty > 0 ? '+' + this.fmt.format((qty * unitPrice) / 100) : '';
      if (descEl) descEl.textContent = level.desc;
      if (range) {
        range.setAttribute(
          'aria-valuetext',
          qty > 0 ? level.name + ', ' + qty + ' ' + unit + ', más ' + this.fmt.format((qty * unitPrice) / 100) : level.name
        );
      }
    }

    /** Riel: relleno y punto. Lo comparten el deslizador y el interruptor. */
    #paintTrack(row, qty, max) {
      // Una sola variable manda el relleno Y el punto. Antes se escribía el
      // porcentaje en cada uno por separado y el punto quedaba medio ancho
      // corrido en los extremos: en 0 se salía del panel por la izquierda.
      const frac = max > 0 ? Math.min(qty / max, 1) : 0;
      row.style.setProperty('--ms-frac', String(frac));
      const range = row.querySelector('[data-ms-range]');
      if (range && Number(range.value) !== qty) range.value = String(qty);
    }

    /**
     * Interruptor: prende/apaga y, cuando está prendido, muestra el MISMO
     * deslizador que el otro complemento — se mide en la misma unidad, así que
     * no tiene por qué usar otro control.
     */
    #renderSwitch(row, qty) {
      const on = qty > 0;
      const max = Number(row.dataset.max) || 20;
      const sw = row.querySelector('[data-ms-switch]');
      if (sw) sw.setAttribute('aria-checked', on ? 'true' : 'false');
      const revealed = row.querySelector('[data-ms-quick]');
      if (revealed) revealed.toggleAttribute('data-off', !on);

      this.#paintTrack(row, qty, max);

      const unit = row.dataset.addonUnit || '';
      const unitPrice = Number(row.dataset.unitPrice) || 0;
      const nameEl = row.querySelector('[data-ms-level-name]');
      const addEl = row.querySelector('[data-ms-level-add]');
      if (nameEl) nameEl.textContent = qty + ' ' + unit;
      if (addEl) addEl.textContent = qty > 0 ? '+' + this.fmt.format((qty * unitPrice) / 100) : '';
    }

    #render() {
      let total = 0;
      for (const row of this.rows) {
        const qty = Number(row.dataset.quantity) || 0;
        const unit = Number(row.dataset.unitPrice) || 0;   // en centavos
        const max = Number(row.dataset.max) || 30;
        total += qty * unit;

        const out = row.querySelector('.ms-addons__value');
        if (out) out.textContent = qty + ' ' + (out.dataset.unit || '');

        const minus = row.querySelector('button[data-step="-1"]');
        const plus = row.querySelector('button[data-step="1"]');
        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = qty >= max;
        row.classList.toggle('ms-addons__row--on', qty > 0);

        if (row.dataset.control === 'slider') this.#renderSlider(row, qty, max);
        if (row.dataset.control === 'switch') this.#renderSwitch(row, qty);
      }

      if (this.totalEl) {
        const val = this.totalEl.querySelector('.ms-addons__total-value');
        if (val) val.textContent = '+' + this.fmt.format(total / 100);
        this.totalEl.hidden = total <= 0;
      }
    }
  }

  customElements.define('ms-performance-addons', MsPerformanceAddons);
})();
